import { useSSRHook } from "ssr-hook";
import { lazy, Suspense, useRef, useState } from "react";
import Lightbox from "react-spring-lightbox";

import { FileIcon, InputDate, Loading, Remove } from ".";
import { useLang } from "./Language";
import { mdiDrag, mdiFile, mdiFloppy, mdiPencil, mdiPlus, mdiTranslateOff } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { getFileName, toBase64 } from "../services/common";
import { useUser } from "../User";

import { Content, ContentFile, Gallery, ID } from "../../types";
import { handleContentFiles } from "../services/editor";
import { api } from "../services/api";
import Modal from "react-responsive-modal";
import { DraggableContextType } from "react-tiny-dnd/lib/types";
import { Draggable, moveItems, useDraggable, useDraggableContext } from "react-tiny-dnd";
import { resizeImage } from "easy-image-resizer";

const Editor = lazy(() => import("../components/Editor"));

export default function Content({ id }: { id: ID }) {
    const lang = useLang();
    const user = useUser();

    const [content, error, isLoading, reload] = useSSRHook<Content>(`/api/content?contentId=${decodeURIComponent(id)}`);

    const [message, setMessage] = useState("");

    const [edit, setEdit] = useState<null | Content>(null);
    const editorRef = useRef<HTMLDivElement>(null);

    const [openGallery, setOpenGallery] = useState<null | { gallery: Gallery, pictureIndex: number }>(null);
    const [editGallery, setEditGallery] = useState<null | Gallery & { index: number }>(null);
    const inputGalleryRef = useRef<HTMLInputElement>(null);

    // drag list
    const onDrop = async (dragIndex: number, overIndex: number) => {
        if (!content) return;
        if (!editGallery) return;

        const pictures = moveItems(editGallery.pictures, dragIndex, overIndex);

        const [, error] = await api.updateContent({
            ...content,
            galleries: content.galleries.map((g, i) => i === editGallery.index ? { ...g, pictures } : g),
        });

        if (error) {
            setMessage("Úprava pořadí obrázků selhalo");
            return;
        }

        setEditGallery({ ...editGallery, pictures })

        setMessage("Pořadí obrázků upraveno");
        reload();
    };
    const context = useDraggableContext({ onDrop });

    const [editFile, setEditFile] = useState<null | ContentFile & { index: number }>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const usedLang = lang === "cs"
        ? "cs"
        : content?.pl.content.trim() ? "pl" : "cs";

    const langContent = content?.[usedLang];

    async function handleSave() {
        setMessage("Ukládám...");

        if (!content) return;
        if (!edit) return;
        if (!editorRef.current) {
            setMessage("Problém s editorem");
            return;
        }

        const contentFiles = await handleContentFiles(edit[lang], editorRef.current);

        const [, error] = await api.updateContent({
            ...content, // not edit because it is handled separately with files and galleries
            [lang]: contentFiles,
        });

        if (error) {
            setMessage("Ukládání změn selhalo");
            return;
        }

        reload();
        setMessage("Stránka úspěšně uložena");
        setEdit(null);
    }

    async function handleSaveGallery() {
        if (!editGallery) return;
        if (!edit) return;
        if (!content) return;

        if (editGallery.index > -1) {
            // update
            const [, error] = await api.updateContent({
                ...content,
                galleries: content.galleries.map((g, i) => i === editGallery.index ? editGallery : g)
            });
            if (error) {
                setMessage("Přidání galerie selhalo");
                return;
            }
            setMessage("Galerie přidána");
        } else {
            // add
            const [, error] = await api.updateContent({
                ...content,
                galleries: [...content.galleries, editGallery],
            });
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Galerie úspěšně uložena")
        }

        reload();
        setEditGallery(null);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function handleAddPictures(e: any) {
        if (!editGallery) return;
        if (!content) return;

        const pictures: Gallery["pictures"] = [];

        const files = e.target.files as File[];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;

            pictures.push({
                full: {
                    type: "EASY_DB_FILE", url: await resizeImage(file, {
                        maxWidth: 1920,
                        maxHeight: 1920,
                        type: "webp",
                    })
                },
                thumb: {
                    type: "EASY_DB_FILE", url: await resizeImage(file, {
                        // width: 235 * 2,
                        maxHeight: 169 * 2,
                        type: "webp",
                    })
                },
            });
        }

        if (pictures.length === 0) return;

        const gallery = {
            ...editGallery,
            pictures: [...editGallery.pictures, ...pictures],
        }

        const [, error] = await api.updateContent({
            ...content,
            galleries: content.galleries.map((g, i) => i !== editGallery.index ? g : gallery),
        });
        if (error) {
            setMessage("Přidávání fotografií selhalo");
            return;
        }

        setEditGallery(gallery);

        reload();
        setMessage("Fotografie byly přidány");
    }

    async function handleRemovePicture(index: number) {
        if (!editGallery) return;
        if (!content) return;

        const gallery = {
            ...editGallery,
            pictures: editGallery.pictures.filter((_, i) => i !== index),
        }

        const [, error] = await api.updateContent({
            ...content,
            galleries: content.galleries.map((g, i) => i !== editGallery.index ? g : gallery),
        });
        if (error) {
            setMessage("Mazání fotografie selhalo");
            return;
        }

        setEditGallery(gallery);

        reload();
        setMessage("fotografie smazána smazána");
    }

    async function handleRemoveGallery(index: number) {
        if (!content) return;
        const [, error] = await api.updateContent({
            ...content,
            galleries: content.galleries.filter((_, i) => i !== index),
        });
        if (error) {
            setMessage("Mazání galerie selhalo");
            return;
        }

        reload();
        setMessage("Galerie smazána");
    }

    async function handleSaveFile() {
        if (!editFile) return;
        if (!content) return;

        if (editFile.index > -1) {
            // update file
            const [, error] = await api.updateContent({
                ...content,
                files: content.files.map((f, i) => i === editFile.index ? editFile : f)
            });
            if (error) {
                setMessage("Přidání souboru selhalo");
                return;
            }
            setMessage("Soubor přidán");
        } else {
            // add file
            const [, error] = await api.updateContent({
                ...content,
                files: [...content.files, editFile],
            });
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Soubor úspěšně uložen")
        }

        reload();
        setEditFile(null);
    }

    async function handleRemoveFile(index: number) {
        if (!content) return;
        const [, error] = await api.updateContent({
            ...content,
            files: content.files.filter((_, i) => i !== index),
        });
        if (error) {
            setMessage("Mazání souboru selhalo");
            return;
        }

        reload();
        setMessage("Soubor smazán");
    }

    return <div className="content-element">

        {user.isAdmin && <>
            <div className="admin small">
                {!edit && <button onClick={() => setEdit(content)}><Icon path={mdiPencil} size={1} /> Upravit obsah</button>}
                {edit && <>
                    <button className="save-button" onClick={handleSave}><Icon path={mdiFloppy} size={1} /> Uložit změny</button>
                </>}
            </div>

            {message && <p className="alert">{message}</p>}
        </>}

        {isLoading && <Loading />}
        {error && <p className="alert">Obsah se nepodařilo načíst 🙁</p>}
        {content && langContent && <>

            {/* -------------- Content ----------------- */}
            {lang === "pl" && usedLang !== "pl" && <div className="no-translation">
                <Icon path={mdiTranslateOff} size={1} /> Ta strona nie ma tłumaczenia
            </div>}

            {!edit && <div className="html-content ck-content" dangerouslySetInnerHTML={{
                __html: langContent.content,
            }} />}
            {edit && <div ref={editorRef}>
                <Suspense fallback={<Loading />}>
                    <Editor
                        value={edit[lang].content}
                        onChange={(content) => setEdit({ ...edit, [lang]: { ...edit[lang], content } })}
                    />
                </Suspense>
            </div>}


            {/* -------------- Photo galleries ----------------- */}
            {content.galleries.length > 0 && <div className="galleries">
                {content.galleries.map((gal, index) => <div
                    key={index}
                    className="gallery"
                    style={{ backgroundImage: `url(${gal.pictures[0]?.full.url || "/logo-black.webp"})` }}
                >
                    {user.isAdmin && edit && <div className="admin small">
                        <button className="icon" onClick={() => setEditGallery({ ...gal, index })}>
                            <Icon path={mdiPencil} size={1} />
                        </button>
                        <Remove onRemove={() => handleRemoveGallery(index)} />
                    </div>}
                    <div className="inner" onClick={() => setOpenGallery({ gallery: gal, pictureIndex: 0 })}>
                        <div className="date">{moment(gal.date).format("LL")}</div>
                        <h5 className="title">{gal.title}</h5>
                    </div>
                </div>)}

                <Lightbox
                    singleClickToZoom
                    isOpen={openGallery !== null}
                    currentIndex={openGallery?.pictureIndex || 0}
                    images={(openGallery?.gallery.pictures || []).map((f, i) => ({
                        src: f.full.url,
                        alt: `${openGallery?.gallery.title} ${i}`
                    }))}
                    onNext={() => {
                        if (openGallery) {
                            let next = openGallery.pictureIndex + 1;
                            if (next >= openGallery.gallery.pictures.length) next = openGallery.gallery.pictures.length - 1;
                            setOpenGallery({ ...openGallery, pictureIndex: next });
                        }
                    }}
                    onPrev={() => {
                        if (openGallery) {
                            let prev = openGallery.pictureIndex - 1;
                            if (prev < 0) prev = 0;
                            setOpenGallery({ ...openGallery, pictureIndex: prev });
                        }
                    }}
                    onClose={() => setOpenGallery(null)}
                    style={{ background: "rgba(0, 0, 0, 0.5)", zIndex: 1000 }}
                    renderHeader={() => <div className="gallery-header"><h1>{openGallery?.gallery.title}</h1></div>}
                    renderFooter={() => <div className="gallery-thumbs">
                        <div className="images">
                            {openGallery?.gallery.pictures.map((f, i) => <img
                                key={i}
                                src={f.thumb.url}
                                onClick={() => setOpenGallery({ ...openGallery, pictureIndex: i })}
                            />)}
                        </div>
                    </div>}
                />
            </div>}
            {user.isAdmin && edit && <>
                <button onClick={() => setEditGallery({ index: -1, title: "", date: moment().format("YYYY-MM-DD"), pictures: [] })} className="center small">
                    <Icon path={mdiPlus} size={1} /> Přidat fotogalerii
                </button>

                <Modal
                    open={editGallery !== null}
                    onClose={() => setEditGallery(null)}
                    center
                    styles={{ modal: { width: "500px" } }}
                >
                    {message && <p className="alert">{message}</p>}
                    {editGallery && <>
                        <h2>Přidat fotogalerii</h2>
                        <input
                            type="text"
                            placeholder="Název fotogalerie"
                            value={editGallery.title}
                            onChange={(e) => setEditGallery({ ...editGallery, title: e.target.value })}
                        />
                        <InputDate
                            value={editGallery.date}
                            onChange={(date) => setEditGallery({ ...editGallery, date })}
                        />

                        <div className="list slim">
                            {editGallery.pictures.map((image, i) => (
                                <DraggableItem
                                    key={i}
                                    context={context}
                                    image={image.thumb.url}
                                    index={i}
                                    onRemove={() => handleRemovePicture(i)}
                                />
                            ))}
                        </div>
                        <button
                            style={{ width: "100%" }}
                            onClick={() => inputRef.current?.click()}
                        >
                            <Icon path={mdiPlus} size={1} />{" "}
                            Přidat obrázky
                        </button>
                        <input
                            style={{ display: "none" }}
                            type="file"
                            ref={inputRef}
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={handleAddPictures}
                        />

                        <div className="divider" />

                        <div className="buttons">
                            <button onClick={handleSaveGallery}>Uložit galerii</button>
                        </div>
                    </>}
                </Modal>
            </>}


            {/* -------------- Files ----------------- */}
            {content.files.length > 0 && <div className="files">
                <ul className="list-download">
                    {content.files
                        .map((file, index) => ({ ...file, index }))
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map((d) => <li key={d.index}>
                            <FileIcon url={d.file.url} size={2} />
                            <span className="name">{d.name}</span>
                            <span className="date">{moment(d.date).format("LL")}</span>
                            <a className="button black" href={d.file.url} target="__blank" download={getFileName(d.name, d.file.url)}>
                                Stáhnout
                            </a>
                            {user.isAdmin && edit && <>
                                <button className="icon" onClick={() => setEditFile(d)}>
                                    <Icon path={mdiPencil} size={1} />
                                </button>
                                <Remove onRemove={() => handleRemoveFile(d.index)} />
                            </>}
                        </li>)}
                </ul>
            </div>}
            {user.isAdmin && edit && <>
                <button className="center small" onClick={() => inputRef.current?.click()}><Icon path={mdiPlus} size={1} />Přidat soubor</button>
                <input
                    className="hidden"
                    ref={inputRef}
                    type="file"
                    placeholder="Nahrát soubor"
                    onChange={async (e) => {
                        const file = e.target.files?.[0] as File;
                        if (!file) return;

                        const url = await toBase64(file);

                        setMessage("");
                        setEditFile({
                            index: -1,
                            name: file.name,
                            date: moment().format("YYYY-MM-DD"),
                            file: {
                                type: "EASY_DB_FILE",
                                url,
                            },
                        });
                    }}
                />

                <Modal
                    open={editFile !== null}
                    onClose={() => setEditFile(null)}
                    center
                    styles={{ modal: { width: "500px" } }}
                >
                    {message && <p className="alert">{message}</p>}
                    {editFile && <>
                        <h2>Soubor</h2>
                        <button onClick={() => inputRef.current?.click()}>
                            <Icon path={mdiFile} size={1} />Nahrát jiný soubor
                        </button>
                        <p>Soubor může mít maximálně 10MB!</p>
                        <InputDate
                            value={editFile.date}
                            onChange={(date) => setEditFile({ ...editFile, date })}
                        />
                        <input
                            type="text"
                            placeholder="Název"
                            value={editFile.name}
                            onChange={(e) => setEditFile({ ...editFile, name: e.target.value })}
                        />
                        <div className="divider" />
                        <div className="buttons">
                            <button onClick={handleSaveFile}>Uložit soubor</button>
                        </div>
                    </>}
                </Modal>
            </>}
        </>}
    </div>;
}

function DraggableItem(
    { context, index, image, onRemove }: {
        context: DraggableContextType;
        index: number;
        image: string;
        onRemove: () => void;
    },
) {
    const { listeners, isDragging } = useDraggable(context, index);

    return (
        <Draggable
            context={context}
            index={index}
            dividerClassName="dnd-divider"
        >
            <div className="item" style={{ opacity: isDragging ? 0.4 : 1 }}>
                <div className="dnd-button" {...listeners}>
                    <Icon path={mdiDrag} size={2} />
                </div>
                <img
                    className="carousel-edit-image"
                    src={image}
                />
                <div className="grow" />
                <Remove onRemove={onRemove} />
            </div>
        </Draggable>
    );
}
